import { useSectionAccess } from "../../auth/useSectionAccess";
import { useState } from "react";
import type { FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { tmApi, type TmProjectStatus } from "../../api/tm";
import { useToast } from "../../components/Toast";
import { Select } from "../../components/Select";
import type { ApiError } from "../../api/client";

export default function TmProjectNewPage() {
  const { canWrite } = useSectionAccess("tm_projects");
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();

  const [busy, setBusy] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState<TmProjectStatus>("PLANNING");
  const [startDate, setStartDate] = useState("");
  const [targetEndDate, setTargetEndDate] = useState("");

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!canWrite) return;
    setBusy(true);
    try {
      const created = await tmApi.createProject({
        name,
        description: description || null,
        status,
        startDate: startDate || null,
        targetEndDate: targetEndDate || null,
      });
      toast.success(t("tm.projects.toastCreated", "Project created."));
      navigate(`/tm/projects/${created.id}`, { replace: true });
    } catch (err) {
      toast.error((err as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} style={{ maxWidth: 720, margin: "0 auto" }}>
      <div className="page-head" style={{ marginBottom: 16 }}>
        <h1 style={{ margin: 0, fontSize: 22, color: "var(--ink)" }}>
          {t("tm.projects.new.title", "New project")}
        </h1>
      </div>
      <div className="card">
        <div className="card-b" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <label className="field">
            <span>{t("tm.projects.new.nameLabel", "Name")} *</span>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} required maxLength={200} />
          </label>
          <label className="field">
            <span>{t("tm.projects.new.description", "Description")}</span>
            <textarea className="input" value={description} onChange={(e) => setDescription(e.target.value)} rows={4} />
          </label>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
            <label className="field">
              <span>{t("tm.projects.new.status", "Status")}</span>
              <Select<TmProjectStatus>
                value={status}
                onChange={setStatus}
                ariaLabel={t("tm.projects.new.status", "Status")}
                options={[
                  { value: "PLANNING", label: t("tm.project.status.PLANNING", "Planning") },
                  { value: "ACTIVE", label: t("tm.project.status.ACTIVE", "Active") },
                  { value: "ON_HOLD", label: t("tm.project.status.ON_HOLD", "On hold") },
                  { value: "COMPLETED", label: t("tm.project.status.COMPLETED", "Completed") },
                  { value: "CANCELLED", label: t("tm.project.status.CANCELLED", "Cancelled") },
                ]}
              />
            </label>
            <label className="field">
              <span>{t("tm.projects.new.startDate", "Start")}</span>
              <input type="date" className="input" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </label>
            <label className="field">
              <span>{t("tm.projects.new.targetEnd", "Target end")}</span>
              <input type="date" className="input" value={targetEndDate} onChange={(e) => setTargetEndDate(e.target.value)} />
            </label>
          </div>
        </div>
        <div style={{
          display: "flex", gap: 8, justifyContent: "flex-end",
          padding: "14px 18px", borderTop: "1px solid var(--border)",
          background: "var(--surface-2)", borderRadius: "0 0 var(--r-md) var(--r-md)",
        }}>
          <button type="button" className="btn" onClick={() => navigate(-1)}>
            {t("action.cancel", "Cancel")}
          </button>
          <button type="submit" className="btn primary" disabled={busy || !canWrite}>
            {busy ? t("tm.projects.new.creating", "Creating…") : t("tm.projects.new.create", "Create project")}
          </button>
        </div>
      </div>
    </form>
  );
}
